import { replaceAll, twoDecimals } from '@utils/utils';
import { Injectable } from '@angular/core';
import {
  QuotationBoundariesDTO,
  QuotationDTO,
  PositionDTO,
  CustomerDTO,
  PositionCreateDTO,
  ChecklistDTO,
  BankAccountLinkDTO,
  FormDTO,
  DomainListDTO,
  DigitalSignStartDTO
} from '@core/modules/api/models';
import { OutputAdapterService } from '@core/modules/api/services/output-adapter/output-adapter.service';

@Injectable({
  providedIn: 'root'
})
export class OutputAdapterCustomService extends OutputAdapterService {
  constructor() {
    super();
  }

  getQuotationsBoundaries(output: any): QuotationBoundariesDTO {
    return {
      financedMaxAmount: output.financedMaxAmount,
      financedMinAmount: output.financedMinAmount,
      financedStartAmount: 6000,
      instalmentMaxAmount: 1000,
      instalmentMinAmount: 50,
      instalmentStartAmount: 125,
      instalmentSteps: 50,
      durationMin: output.installmentNumber[0] || 0,
      durationMax:
        output.installmentNumber[output.installmentNumber.length - 1] || 0,
      durationStart: output.installmentNumber[0] || 0,
      durationSteps: output.durationSteps,
      purposes: output.loanPurpose.map(x => ({
        ...x,
        description:
          x.description.charAt(0).toUpperCase() +
          x.description.slice(1).toLowerCase()
      })),
      amountSteps: 500,
      purposeStart: 'LIQ'
    };
  }

  postQuotations(output: any): QuotationDTO {
    return this.quotationAdapter(
      output.quotationData[0],
      output.quotationData[0].quotationBaseData
    );
  }

  postPosition(output: any): PositionCreateDTO {
    if (location.hostname.indexOf('localhost') > -1) {
      output.redirectUrl = replaceAll(
        output.redirectUrl,
        'https://credem-sviluppo/self-digital-fe',
        location.origin
      );
    }

    return output;
  }

  getPosition(output: any): PositionDTO {
    return output;
  }

  getPositionQuotation(output: any): QuotationDTO {
    return this.quotationAdapter(output, output.quotationBaseData || {});
  }

  getPositionCustomer(output: any): CustomerDTO {
    return {
      email: output.email,
      firstName: output.firstName,
      lastName: output.lastName,
      mobilePhone: output.mobilePhone,
      ndg: output.ndg,
      positionId: output.positionId,
      taxNumber: output.taxNumber
    };
  }

  getDomain(output: any): DomainListDTO {
    return {
      domainList: output.domainList.map(item => {
        const domainItemValue = item.value.toLowerCase();

        return {
          ...item,
          value:
            domainItemValue.charAt(0).toUpperCase() + domainItemValue.slice(1)
        };
      })
    };
  }

  getDigitalSignatureRequestDsp(output: any): DigitalSignStartDTO {
    if (location.hostname.indexOf('localhost') > -1) {
      output.redirectURL = replaceAll(
        output.redirectURL,
        'https://credem-sviluppo/self-digital-fe',
        location.origin
      );
    }

    return {
      providerUrl: output.redirectURL,
      providerMethod: output.redirectMethod,
      attributes: output.attributes
    };
  }

  getChecklist(output: any): ChecklistDTO {
    return {
      ...output,
      checklistId: output.checkListId,
      entities: output.entities.map(entity => ({
        ...entity,
        documents: entity.documents.map(doc => ({
          ...doc,
          documentCod: doc.documentCode,
          documentDesc: doc.documentDescritpion,
          listDocAttribute: doc.attributes,
          categoryCod: doc.categoryCode,
          categoryDesc: doc.categoryDescritpion,
          flagAcquireOriginal: doc.flagAcquiredOrigin,
          lastUpload: doc.files &&
            doc.files[0] && {
              documentCod: doc.files[0].docCod,
              fileId: doc.files[0].fileId,
              fileName: doc.files[0].fileName,
              repositoryId: doc.files[0].storageId,
              uploadedAt: doc.files[0].uploadTs,
              uploadedBy: doc.files[0].uploadUser,
              versionId: doc.files[0].versionId
            }
        }))
      }))
    };
  }

  postPsd2Start(output: any): BankAccountLinkDTO {
    if (
      location.hostname.indexOf('localhost') > -1 &&
      output &&
      output.redirectURL
    ) {
      const baseUrl = location.origin;
      output.redirectUrl = `${baseUrl}/assets/demo/bank-evaluation.html?baseUrl=${baseUrl}`;
    } else {
      output.redirectUrl = output.redirectURL;
    }

    return output;
  }

  getForm(output: any): FormDTO[] {
    return output && output.map(form => ({ ...form, titleVisible: true }));
  }

  private quotationAdapter(data: any, deepData: any): QuotationDTO {
    return {
      duration: deepData.loanDuration,
      durationInYears: Math.ceil(deepData.loanDuration / 12),
      finality: deepData.loanPurpose,
      dueAmount: data.dueAmount,
      disbursedAmount: deepData.disbursedAmount,
      financedAmount: deepData.financedAmount,
      requestedAmount: deepData.requestedAmount,
      netDisbursedAmountToCustomer: deepData.netDisbursedAmountToCustomer,
      totalCostOfCredit: twoDecimals(
        data.dueAmount - deepData.financedAmount + data.managementFees
      ),
      financialProduct: deepData.financialProduct,
      installment: deepData.loanInstallment,
      taeg: data.taeg,
      tan: deepData.tan,
      teg: data.teg,
      firstInstallmentDate: data.firstInstallmentDate,
      singleCommunicationCost: data.singleCommunicationCost,
      totalCommunicationCost: data.totalCommunicationCost,
      managementFees: data.managementFees,
      guaranteeFee: data.guaranteeFee,
      installmentFee: data.installmentFee,
      totalInstallmentFee: twoDecimals(
        data.installmentFee * deepData.loanDuration
      ),
      singleBankReportingStamp: data.singleBankReportingStamp,
      totalBankReportingStamp: data.totalBankReportingStamp
    };
  }
}
